/**
 * Wohnungsverbund im Belegungskalender — reine Funktionen ohne React.
 *
 * Der Server liefert je Nacht nur, dass der Verbund sie belegt, nie wer oder womit. Darauf baut
 * hier alles auf: Zellzustand, Einrückung und die Ablehnung eines Ziels, bevor der Server gefragt
 * wird. Entscheiden tut weiter der Server; das hier spart nur den sicheren Fehlschlag.
 */
import type { CalendarDayCell, CalendarPropertyRow } from '@/shared/api';
import type { BookingSpan } from './calendarHelpers';

export type CalendarCellState = 'booked' | 'doubleBooked' | 'family' | 'free';

export interface CalendarRowLayout {
  row: CalendarPropertyRow;
  /** Zimmer, dessen Gesamtwohnung direkt darüber steht — wird eingerückt. */
  isRoom: boolean;
}

/**
 * Die eigene Buchung bleibt im Bild; belegt der Verbund dieselbe Nacht, ist die Wohnung doppelt
 * belegt — das versteckt die Buchung nicht. Eine Verbundnacht ist sonst kein freies Angebot.
 */
export function calendarCellState(cell: CalendarDayCell): CalendarCellState {
  if (cell.status === 'booked') return cell.occupiedByFamily ? 'doubleBooked' : 'booked';
  return cell.occupiedByFamily ? 'family' : 'free';
}

const wholeFlatOf = (row: CalendarPropertyRow) => row.flatFamily?.wholeFlatPropertyId ?? null;

/**
 * Gesamtwohnung oben, ihre Zimmer direkt darunter; sonst bleibt die Reihenfolge des Servers. Ist
 * die Gesamtwohnung ausgefiltert, steht ein Zimmer an seinem Platz und wird nicht eingerückt.
 */
export function nestCalendarRows(rows: CalendarPropertyRow[]): CalendarRowLayout[] {
  const shown = new Set(rows.map((row) => row.propertyId));
  const isNestedRoom = (row: CalendarPropertyRow) => {
    const wholeFlat = wholeFlatOf(row);
    return wholeFlat !== null && shown.has(wholeFlat);
  };
  return rows
    .filter((row) => !isNestedRoom(row))
    .flatMap((row) => [
      { row, isRoom: false },
      ...rows
        .filter((room) => wholeFlatOf(room) === row.propertyId)
        .map((room) => ({ row: room, isRoom: true })),
    ]);
}

export function flatFamilyLabel(row: CalendarPropertyRow): string | null {
  if (row.flatFamily?.role === 'WholeFlat') return 'Gesamtwohnung';
  if (row.flatFamily?.role === 'Room') return 'Zimmer';
  return null;
}

const related = (left: CalendarPropertyRow, right: CalendarPropertyRow) =>
  wholeFlatOf(left) === right.propertyId || wholeFlatOf(right) === left.propertyId;

/**
 * Warum das Ziel die gezogene Buchung nicht aufnehmen kann: eine sichtbare Nacht des Aufenthalts
 * ist dort durch den Verbund belegt. Gehören Quelle und Ziel zueinander, kann diese Belegung die
 * gezogene Buchung selbst sein — dann prüft allein der Server, der sie dabei ausnimmt.
 */
export function familyDropRefusal(
  target: CalendarPropertyRow,
  source: CalendarPropertyRow | undefined,
  span: Pick<BookingSpan, 'startIndex' | 'nights'>,
): string | null {
  if (!source || target.propertyId === source.propertyId || related(target, source)) return null;
  const nights = target.cells.slice(span.startIndex, span.startIndex + span.nights);
  if (!nights.some((cell) => cell.occupiedByFamily)) return null;
  const cause =
    target.flatFamily?.role === 'Room'
      ? 'die Gesamtwohnung ist gebucht oder gesperrt'
      : 'ein Zimmer ist gebucht oder gesperrt';
  return `„${target.propertyName}“ ist in diesem Zeitraum durch den Wohnungsverbund belegt: ${cause}.`;
}

/** Je Ziel-Apartment der Grund, aus dem „Apartment wechseln“ es nicht anbietet. */
export function familyRefusals(
  rows: CalendarPropertyRow[],
  sourcePropertyId: string,
  span: Pick<BookingSpan, 'startIndex' | 'nights'>,
): Map<string, string> {
  const source = rows.find((row) => row.propertyId === sourcePropertyId);
  const refusals = new Map<string, string>();
  for (const row of rows) {
    const reason = familyDropRefusal(row, source, span);
    if (reason) refusals.set(row.propertyId, reason);
  }
  return refusals;
}
